import { c as _c } from "react/compiler-runtime";
import { usePageView } from '@/analytics/pageTypes';
import { SeoHeader } from '@/components/SeoHeader';
import { Layout } from '@/components/layout';
import Hero from '@/sections/Hero';
import { getIntlProps } from '@/utils/intl';
import type { GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';
import { SectionDelimiter } from '@/components/SectionDelimiter';
import React from 'react';
import { Career } from '@/sections/aboutUs/Career';
import { Ceo } from '@/sections/aboutUs/Ceo';
import { Facts } from '@/sections/aboutUs/Facts';
import { Faq } from '@/sections/aboutUs/Faq';
import { MoreInfo } from '@/sections/aboutUs/MoreInfo';
import { OurMission } from '@/sections/aboutUs/OurMission';
import { Social } from '@/sections/aboutUs/Social';
import { Team } from '@/sections/aboutUs/Team';
import { Trust } from '@/sections/aboutUs/Trust';
import { WroteAboutUs } from '@/sections/aboutUs/WroteAboutUs';
import { useGetWroteAboutUsQuery } from '@/api/sanity/generated';
import { Job } from '@/types/job';
import { mapWroteAboutUsToArticles } from '@/sections/wroteAboutUs/mapWroteAboutUsToArticles';
import { ArticleBoxProps } from '@/components/boxes';
import { getPageBannerBySlug, PageBanner } from '@/api/sanity/pageBanners';
import { useIsSelectedLanguage } from '@/hooks/useIsSelectedLanguage';
import { getAllPageMenuPromoBanners, PageMenuPromoBanner } from '@/api/sanity/pageMenuPromoBanner';
import { getJobs } from '@/sections/jobs/functions/getJobs';
import { sanityLanguage } from '@/constants/language';
interface Props {
  pageBanner: PageBanner | null;
  jobs: Job[];
  jobsCount: string;
  wroteAboutUsItems?: ArticleBoxProps[];
  menuPromoBanners?: PageMenuPromoBanner[];
}
export default function AboutUs(t0) {
  const $ = _c(60);
  const {
    jobs,
    jobsCount,
    menuPromoBanners,
    wroteAboutUsItems,
    pageBanner
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = ["aboutUs", "core"];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation(t1);
  usePageView("about-us");
  const isCsLanguage = useIsSelectedLanguage("cs");
  let t2;
  if ($[1] !== t) {
    t2 = t("seo.title");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t) {
    t3 = t("seo.description");
    $[3] = t;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2 || $[6] !== t3) {
    t4 = <SeoHeader title={t2} description={t3} />;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = pageBanner ? pageBanner.videoId : "VggNOGNSdSg";
  let t6;
  if ($[8] !== pageBanner?.title || $[9] !== t) {
    t6 = pageBanner?.title ?? t("banner.title");
    $[8] = pageBanner?.title;
    $[9] = t;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== pageBanner?.text || $[12] !== t) {
    t7 = pageBanner?.text ?? t("banner.text");
    $[11] = pageBanner?.text;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = pageBanner ? pageBanner.buttons : null;
  const t9 = pageBanner?.image?.url ?? "/images/sections/aboutUs/hero/hero.png";
  let t10;
  if ($[14] !== pageBanner?.image?.alt || $[15] !== t) {
    t10 = pageBanner?.image?.alt ?? t("banner.title");
    $[14] = pageBanner?.image?.alt;
    $[15] = t;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = {
      url: t9,
      alt: t10
    };
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  const t12 = pageBanner?.variant || "video";
  let t13;
  if ($[20] !== t11 || $[21] !== t12 || $[22] !== t5 || $[23] !== t6 || $[24] !== t7 || $[25] !== t8) {
    t13 = <Hero videoId={t5} title={t6} text={t7} buttons={t8} image={t11} variant={t12} />;
    $[20] = t11;
    $[21] = t12;
    $[22] = t5;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
    $[26] = t13;
  } else {
    t13 = $[26];
  }
  let t14;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <div className="bg-blue-50"><SectionDelimiter color="bg-blue-100" background="bg-transparent" /></div>;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <div className="bgCircle absolute top-1/3 left-1/2 hidden translate-x-1/2 -translate-y-1/2 lg:block" />;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  let t17;
  let t18;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <OurMission />;
    t17 = <Facts />;
    t18 = <Trust />;
    $[29] = t16;
    $[30] = t17;
    $[31] = t18;
  } else {
    t16 = $[29];
    t17 = $[30];
    t18 = $[31];
  }
  let t19;
  if ($[32] !== isCsLanguage || $[33] !== wroteAboutUsItems) {
    t19 = isCsLanguage && wroteAboutUsItems && <WroteAboutUs items={wroteAboutUsItems} />;
    $[32] = isCsLanguage;
    $[33] = wroteAboutUsItems;
    $[34] = t19;
  } else {
    t19 = $[34];
  }
  let t20;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <Ceo />;
    $[35] = t20;
  } else {
    t20 = $[35];
  }
  let t21;
  if ($[36] !== t19) {
    t21 = <div className="relative z-30 bg-blue-100 py-10 lg:py-20">{t15}<div className="relative z-10 flex flex-col gap-20 lg:gap-40">{t16}{t17}{t18}{t19}{t20}</div></div>;
    $[36] = t19;
    $[37] = t21;
  } else {
    t21 = $[37];
  }
  let t22;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <Team />;
    $[38] = t22;
  } else {
    t22 = $[38];
  }
  let t23;
  if ($[39] !== isCsLanguage) {
    t23 = isCsLanguage && <div className="bgCircle absolute right-0 bottom-0 hidden h-192 translate-x-1/3 translate-y-1/3 lg:block" />;
    $[39] = isCsLanguage;
    $[40] = t23;
  } else {
    t23 = $[40];
  }
  let t24;
  if ($[41] !== t23) {
    t24 = <div className="relative bg-blue-100 pt-10 pb-20 lg:pt-20 lg:pb-40">{t22}{t23}</div>;
    $[41] = t23;
    $[42] = t24;
  } else {
    t24 = $[42];
  }
  let t25;
  if ($[43] !== isCsLanguage || $[44] !== jobs || $[45] !== jobsCount) {
    t25 = isCsLanguage && <><div className="bg-blue-100"><SectionDelimiter color="bg-blue-50" background="bg-transparent" /></div><div className="relative z-30 bg-blue-50 pt-10 pb-20 lg:pt-20 lg:pb-40"><Social /></div><SectionDelimiter color="bg-blue-700" background="bg-blue-50" /><div className="relative bg-blue-700 pt-10 pb-20 lg:pt-20 lg:pb-40"><div className="bgCircle absolute top-0 left-0 hidden translate-x-14 translate-y-1/4 rotate-180 lg:block" /><div className="bgCircle absolute bottom-32 left-1/2 hidden h-[25rem] w-[25rem] translate-x-1/2 lg:block" /><Career jobs={jobs} jobsCount={jobsCount} /></div></>;
    $[43] = isCsLanguage;
    $[44] = jobs;
    $[45] = jobsCount;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  const t26 = isCsLanguage ? "bg-blue-700" : "bg-blue-100";
  let t27;
  if ($[47] !== t26) {
    t27 = <SectionDelimiter color="bg-blue-50" background={t26} />;
    $[47] = t26;
    $[48] = t27;
  } else {
    t27 = $[48];
  }
  let t28;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t28 = <div className="relative overflow-hidden bg-blue-50 py-10 lg:py-20"><div className="relative z-10 flex flex-col gap-20 lg:gap-40"><Faq /><MoreInfo /></div><div className="bgCircle absolute right-0 bottom-0 translate-x-1/4 translate-y-1/4 rotate-180" /></div>;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== t13 || $[51] !== t21 || $[52] !== t24 || $[53] !== t25 || $[54] !== t27) {
    t29 = <div className="relative overflow-x-hidden">{t13}{t14}{t21}{t24}{t25}{t27}{t28}</div>;
    $[50] = t13;
    $[51] = t21;
    $[52] = t24;
    $[53] = t25;
    $[54] = t27;
    $[55] = t29;
  } else {
    t29 = $[55];
  }
  let t30;
  if ($[56] !== menuPromoBanners || $[57] !== t29 || $[58] !== t4) {
    t30 = <Layout menuPromoBanners={menuPromoBanners}>{t4}{t29}</Layout>;
    $[56] = menuPromoBanners;
    $[57] = t29;
    $[58] = t4;
    $[59] = t30;
  } else {
    t30 = $[59];
  }
  return t30;
}
export const getStaticProps = (async ctx => {
  const fetchWroteAboutUs = useGetWroteAboutUsQuery.fetcher({
    limit: 3,
    i18nLang: sanityLanguage
  });
  const wroteAboutUsData = await fetchWroteAboutUs();
  const wroteAboutUsItems = process.env.NEXT_PUBLIC_LANGUAGE === 'cs' && wroteAboutUsData.allWroteAboutUs && wroteAboutUsData.allWroteAboutUs?.length > 0 && mapWroteAboutUsToArticles(wroteAboutUsData.allWroteAboutUs.map(item => ({
    ...item,
    name: item?.name || '',
    link: item?.link || '',
    date: item?.date || '',
    text: item?.text || ''
  }))).filter(Boolean) || [];
  const {
    jobs,
    total
  } = await getJobs();
  const intlProps = await getIntlProps(ctx, ['core', 'aboutUs', 'testimonials', 'career']);
  return {
    props: {
      menuPromoBanners: await getAllPageMenuPromoBanners(),
      pageBanner: await getPageBannerBySlug('about-us'),
      jobsCount: total,
      jobs,
      wroteAboutUsItems,
      ...intlProps
    }
  };
}) satisfies GetStaticProps<Props>;